import { useEffect, useId, useRef, useState, type RefObject } from "react";
import { createPortal } from "react-dom";
import { useLocation } from "react-router-dom";
import { AccountMenuContent } from "./AccountMenuContent";
import { useAccountMenuModel } from "./useAccountMenuModel";
import type { AccountMenuModel } from "./useAccountMenuModel";

function releaseBodyScrollLock() {
  document.body.style.overflow = "";
  document.body.style.removeProperty("overflow");
}

function useMenuFocusTrap(
  containerRef: RefObject<HTMLElement | null>,
  open: boolean,
  onClose: () => void,
) {
  const triggerRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (!open) return;
    const container = containerRef.current;
    if (!container) return;

    triggerRef.current = document.activeElement as HTMLElement | null;

    const focusables = container.querySelectorAll<HTMLElement>(
      'button, a[href], [tabindex]:not([tabindex="-1"])',
    );
    focusables[0]?.focus();

    function onKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") {
        event.preventDefault();
        onClose();
        triggerRef.current?.focus();
        return;
      }
      if (event.key !== "Tab") return;
      if (!container) return;
      const items = [...container.querySelectorAll<HTMLElement>(
        'button, a[href], [tabindex]:not([tabindex="-1"])',
      )].filter((el) => !el.hasAttribute("disabled"));
      if (items.length === 0) return;
      const first = items[0];
      const last = items[items.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [containerRef, onClose, open]);
}

type MobileAccountMenuSheetProps = {
  open: boolean;
  model: AccountMenuModel;
  onClose(): void;
};

export function MobileAccountMenuSheet({ open, model, onClose }: MobileAccountMenuSheetProps) {
  const sheetRef = useRef<HTMLDivElement>(null);
  const menuId = useId();

  useEffect(() => {
    return () => {
      releaseBodyScrollLock();
    };
  }, []);

  useEffect(() => {
    if (!open) {
      releaseBodyScrollLock();
      return;
    }
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previous;
    };
  }, [open]);

  useMenuFocusTrap(sheetRef, open, onClose);

  if (!open || typeof document === "undefined") return null;

  return createPortal(
    <div className="account-menu-sheet-host" data-account-menu-open="">
      <button
        type="button"
        className="account-menu-sheet-backdrop"
        aria-label="Close account menu"
        onClick={onClose}
      />
      <div
        ref={sheetRef}
        id={menuId}
        className="account-menu account-menu--sheet"
        role="menu"
        aria-label="Account menu"
      >
        <div className="account-menu-sheet-handle" aria-hidden="true" />
        <AccountMenuContent model={model} onClose={onClose} />
      </div>
    </div>,
    document.body,
  );
}

export function MobileAccountTrigger({ className = "" }: { className?: string }) {
  const model = useAccountMenuModel();
  const location = useLocation();
  const [open, setOpen] = useState(false);

  useEffect(() => {
    setOpen(false);
    releaseBodyScrollLock();
  }, [location.pathname]);

  if (!model) return null;

  const close = () => {
    setOpen(false);
    releaseBodyScrollLock();
  };

  return (
    <>
      <button
        type="button"
        className={`mobile-account-trigger staff-header-btn staff-header-btn--account${className ? ` ${className}` : ""}`}
        aria-label="Open account menu"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((value) => !value)}
      >
        <span className="avatar mobile-account-trigger__avatar" aria-hidden="true">
          {model.userInitials}
        </span>
      </button>
      <MobileAccountMenuSheet open={open} model={model} onClose={close} />
    </>
  );
}
